/**
 * FlowHub - Authentication Store
 * Zustand store for managing authentication state
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { auth, db } from '../../../shared/lib/supabase';
import type { UserProfile, AuthUser } from '../../../shared/types';

// Flag global para controlar se está em processo de registro
let isSigningUp = false;

// Flag para controlar se o estado foi rehidratado do localStorage
let hasHydrated = false;

// Mutex para evitar chamadas concorrentes de loadUser
let isLoadingUser = false;
let loadUserPromise: Promise<void> | null = null;

// Função para fazer rollback do usuário auth criado quando o perfil falhar
async function rollbackAuthUser(userId: string) {
  try {
    console.log('🔄 Starting rollback for user:', userId);
    
    // Primeiro fazer logout
    await auth.signOut();
    
    // Tentar deletar o usuário via admin API (se disponível)
    // Nota: Isso requer uma edge function com service_role_key
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ userId, force: true }),
      });
      
      const result = await response.json();
      console.log('🔄 Rollback result:', result);
      
      if (!response.ok) {
        console.warn('Could not delete orphan auth user:', userId, result);
      } else {
        console.log('✅ Orphan auth user deleted:', userId);
      }
    } catch (deleteError) {
      console.warn('Could not delete orphan auth user (edge function not available):', deleteError);
    }
  } catch (error) {
    console.error('Error during auth rollback:', error);
  }
}

interface AuthState {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  
  // MFA state
  mfaRequired: boolean;
  mfaFactorId: string | null;
  pendingSessionUserId: string | null;
  
  // Actions
  signIn: (email: string, password: string) => Promise<void>;
  verifyMfa: (code: string) => Promise<void>;
  signUp: (email: string, password: string, fullName?: string, role?: 'admin' | 'client', cpfCnpj?: string, phone?: string) => Promise<void>;
  signOut: () => Promise<void>;
  loadUser: () => Promise<void>;
  updateProfile: (data: Partial<UserProfile>) => Promise<void>;
  clearMfaState: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isLoading: true,
      isAuthenticated: false,
      mfaRequired: false,
      mfaFactorId: null,
      pendingSessionUserId: null,

      signIn: async (email: string, password: string) => {
        try {
          set({ isLoading: true });

          const { data, error } = await auth.signIn(email, password);

          if (error) throw error;

          if (!data.user) {
            throw new Error('Login falhou: usuário não encontrado');
          }

          // Verificar se MFA é necessário
          const { data: aal } = await auth.mfa.getAuthenticatorAssuranceLevel();
          
          console.log('AAL Status:', {
            currentLevel: aal?.currentLevel,
            nextLevel: aal?.nextLevel,
            currentAuthenticationMethods: aal?.currentAuthenticationMethods,
          });
          
          // Verificar se há fatores MFA configurados
          const { data: factors } = await auth.mfa.listFactors();
          const totpFactor = factors?.totp?.find(f => f.status === 'verified');
          
          console.log('MFA Factors:', {
            totpFactors: factors?.totp?.length || 0,
            verifiedFactor: totpFactor ? totpFactor.id : 'none',
          });
          
          // Se há fator verificado e ainda está em aal1, solicitar MFA
          if (totpFactor && aal?.currentLevel === 'aal1') {
            console.log('MFA required - showing challenge modal');
            set({ 
              mfaRequired: true,
              mfaFactorId: totpFactor.id,
              pendingSessionUserId: data.user.id,
              isLoading: false
            });
            return; // Aguarda entrada do código TOTP
          }

          // MFA não configurado ou já validado - prosseguir com login
          console.log('Loading user - MFA not required or already verified');
          await get().loadUser();

        } catch (error) {
          set({ isLoading: false, mfaRequired: false, mfaFactorId: null, pendingSessionUserId: null });
          throw error;
        }
      },

      verifyMfa: async (code: string) => {
        try {
          const { mfaFactorId } = get();
          
          if (!mfaFactorId) {
            throw new Error('Fator MFA não encontrado');
          }

          set({ isLoading: true });

          // Seguindo documentação oficial: criar challenge primeiro
          const challenge = await auth.mfa.challenge({ factorId: mfaFactorId });
          if (challenge.error) throw challenge.error;

          const challengeId = challenge.data.id;

          // Depois verificar código
          const verify = await auth.mfa.verify({
            factorId: mfaFactorId,
            challengeId,
            code: code,
          });

          if (verify.error) throw verify.error;

          // Código válido - carregar usuário
          set({ 
            mfaRequired: false,
            mfaFactorId: null,
            pendingSessionUserId: null
          });

          await get().loadUser();

        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      clearMfaState: () => {
        set({ 
          mfaRequired: false,
          mfaFactorId: null,
          pendingSessionUserId: null,
          isLoading: false
        });
      },

      signUp: async (
        email: string,
        password: string,
        fullName?: string,
        role: 'admin' | 'client' = 'client',
        cpfCnpj?: string,
        phone?: string
      ) => {
        let createdUserId: string | null = null;
        
        try {
          set({ isLoading: true });
          isSigningUp = true; // Ativar flag para evitar auto-login via onAuthStateChange

          const normalizedEmail = email.trim().toLowerCase();
          const normalizedCpfCnpj = cpfCnpj ? cpfCnpj.replace(/\D/g, '') : undefined;
          const normalizedPhone = phone ? phone.replace(/\D/g, '') : undefined;

          const { data, error } = await auth.signUp(normalizedEmail, password, {
            full_name: fullName || '',
            role,
          });

          if (error) throw error;

          if (data.user) {
            createdUserId = data.user.id;
            
            // Wait a moment for trigger to execute
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Try to get profile
            const { data: profile, error: profileError } = await db.userProfiles.getById(data.user.id);

            let userProfile: UserProfile;
            
            if (profileError || !profile) {
              // Trigger didn't create profile, create manually
              console.warn('Trigger failed, creating profile manually');
              const { data: newProfile, error: createError } = await db.userProfiles.create(
                data.user.id,
                {
                  role: role,
                  full_name: fullName || '',
                  cpf_cnpj: normalizedCpfCnpj || undefined,
                  phone: normalizedPhone || undefined,
                }
              );
              
              if (createError) {
                console.error('Failed to create profile:', createError);
                // Traduzir erros do banco para mensagens amigáveis
                const errorMessage = createError.message || '';
                const errorCode = (createError as any).code;
                const errorHint = (createError as any).hint;
                
                let friendlyError = 'Erro ao criar perfil';
                if (errorHint === 'cpf_cnpj_duplicate' || errorMessage.includes('CPF/CNPJ já cadastrado') || errorMessage.includes('idx_user_profiles_cpf_cnpj')) {
                  friendlyError = 'CPF/CNPJ já cadastrado em outra conta';
                } else if (errorHint === 'phone_duplicate' || errorMessage.includes('Telefone já cadastrado') || errorMessage.includes('idx_user_profiles_phone')) {
                  friendlyError = 'Telefone já cadastrado em outra conta';
                } else if (errorCode === '23505' || errorMessage.includes('duplicate key') || errorMessage.includes('unique constraint')) {
                  // Tentar identificar qual campo
                  if (errorMessage.includes('cpf') || errorMessage.includes('cpf_cnpj')) {
                    friendlyError = 'CPF/CNPJ já cadastrado em outra conta';
                  } else if (errorMessage.includes('phone')) {
                    friendlyError = 'Telefone já cadastrado em outra conta';
                  } else {
                    friendlyError = 'CPF/CNPJ ou telefone já cadastrado em outra conta';
                  }
                }
                
                // CRÍTICO: Fazer rollback ANTES de lançar o erro
                console.log('🔄 Rollback: deleting auth user', createdUserId);
                await rollbackAuthUser(createdUserId);
                
                // Garantir que não está autenticado
                set({ isLoading: false, isAuthenticated: false, user: null });
                
                throw new Error(friendlyError);
              }
              userProfile = newProfile as UserProfile;
            } else {
              // Atualizar perfil com CPF/CNPJ e telefone se fornecidos
              if (cpfCnpj || phone) {
                const { error: updateError } = await db.userProfiles.update(
                  data.user.id,
                  {
                    cpf_cnpj: normalizedCpfCnpj || undefined,
                    phone: normalizedPhone || undefined,
                  }
                );
                
                if (updateError) {
                  console.error('Error updating profile with CPF/phone:', updateError);
                  // Traduzir erros do banco para mensagens amigáveis
                  const errorMessage = updateError.message || '';
                  const errorCode = (updateError as any).code;
                  const errorHint = (updateError as any).hint;
                  
                  let friendlyError = 'Erro ao atualizar perfil';
                  if (errorHint === 'cpf_cnpj_duplicate' || errorMessage.includes('CPF/CNPJ já cadastrado') || errorMessage.includes('idx_user_profiles_cpf_cnpj')) {
                    friendlyError = 'CPF/CNPJ já cadastrado em outra conta';
                  } else if (errorHint === 'phone_duplicate' || errorMessage.includes('Telefone já cadastrado') || errorMessage.includes('idx_user_profiles_phone')) {
                    friendlyError = 'Telefone já cadastrado em outra conta';
                  } else if (errorCode === '23505' || errorMessage.includes('duplicate key') || errorMessage.includes('unique constraint')) {
                    // Tentar identificar qual campo
                    if (errorMessage.includes('cpf') || errorMessage.includes('cpf_cnpj')) {
                      friendlyError = 'CPF/CNPJ já cadastrado em outra conta';
                    } else if (errorMessage.includes('phone')) {
                      friendlyError = 'Telefone já cadastrado em outra conta';
                    } else {
                      friendlyError = 'CPF/CNPJ ou telefone já cadastrado em outra conta';
                    }
                  }
                  
                  // CRÍTICO: Fazer rollback ANTES de lançar o erro
                  console.log('🔄 Rollback: deleting auth user', createdUserId);
                  await rollbackAuthUser(createdUserId);
                  
                  // Garantir que não está autenticado
                  set({ isLoading: false, isAuthenticated: false, user: null });
                  
                  throw new Error(friendlyError);
                }
              }
              
              userProfile = profile as UserProfile;
            }

            // SUCESSO: Somente aqui setamos como autenticado
            set({
              user: {
                id: data.user.id,
                email: data.user.email!,
                profile: userProfile,
              },
              isAuthenticated: true,
              isLoading: false,
            });
          }
        } catch (error) {
          console.error('SignUp error:', error);
          
          // Garantir estado limpo em caso de erro
          set({ isLoading: false, isAuthenticated: false, user: null });
          
          // Garantir logout se houver erro
          try {
            await auth.signOut();
          } catch (signOutError) {
            console.error('Error signing out after failed signup:', signOutError);
          }
          
          throw error;
        } finally {
          isSigningUp = false; // Desativar flag
        }
      },

      signOut: async () => {
        try {
          await auth.signOut();
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
        } catch (error) {
          console.error('Sign out error:', error);
          throw error;
        }
      },

      loadUser: async () => {
        // Mutex: se já está carregando, aguardar a promise existente
        if (isLoadingUser && loadUserPromise) {
          console.log('🔄 [AuthStore] loadUser already in progress, waiting...');
          return loadUserPromise;
        }

        isLoadingUser = true;
        loadUserPromise = (async () => {
        try {
          console.log('🔄 [AuthStore] Starting loadUser...', { hasHydrated });
          
          // Wait for hydration if not yet complete (max 1s with polling)
          if (!hasHydrated) {
            console.log('⏳ [AuthStore] Waiting for hydration...');
            const hydrationStart = Date.now();
            while (!hasHydrated && Date.now() - hydrationStart < 1000) {
              await new Promise(resolve => setTimeout(resolve, 50));
            }
            if (!hasHydrated) {
              console.warn('⚠️ [AuthStore] Hydration wait timed out, proceeding anyway');
            }
          }

          set({ isLoading: true });
          console.log('🔄 [AuthStore] Getting session...');
          
          // Always validate session with Supabase (don't trust persisted state alone)
          const sessionPromise = auth.getSession();
          const timeoutPromise = new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('Session timeout')), 4000)
          );
          
          let session;
          try {
            const result = await Promise.race([sessionPromise, timeoutPromise]);
            session = result.data;
          } catch (timeoutError) {
            console.warn('⚠️ [AuthStore] Session timeout, checking localStorage...');
            // On timeout, check if we have persisted state we can use
            const persistedState = get();
            if (persistedState.user && persistedState.isAuthenticated) {
              console.log('✅ [AuthStore] Using persisted user from localStorage after timeout');
              set({ isLoading: false });
              return;
            }
            console.log('ℹ️ [AuthStore] No persisted user, setting unauthenticated');
            set({ user: null, isAuthenticated: false, isLoading: false });
            return;
          }
          
          console.log('✅ [AuthStore] Session retrieved:', !!session.session);

          if (!session.session) {
            console.log('ℹ️ [AuthStore] No session found, user not logged in');
            set({ user: null, isAuthenticated: false, isLoading: false });
            return;
          }

          console.log('🔄 [AuthStore] Fetching user profile for:', session.session.user.id);
          
          // Add timeout to profile fetch
          const profilePromise = db.userProfiles.getById(session.session.user.id);
          const profileTimeoutPromise = new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('Profile fetch timeout')), 4000)
          );
          
          let profileResult;
          try {
            profileResult = await Promise.race([profilePromise, profileTimeoutPromise]);
          } catch (timeoutError) {
            console.warn('⚠️ [AuthStore] Profile fetch timeout, using persisted state');
            // On timeout, try to use persisted state
            const currentState = get();
            if (currentState.user && currentState.isAuthenticated) {
              console.log('✅ [AuthStore] Using persisted profile from localStorage');
              set({ isLoading: false });
            } else {
              console.log('ℹ️ [AuthStore] No persisted user, setting unauthenticated');
              set({ user: null, isAuthenticated: false, isLoading: false });
            }
            return;
          }
          
          const { data: profile, error } = profileResult;
          console.log('✅ [AuthStore] Profile fetch result:', { hasProfile: !!profile, error: error?.message });

          if (error || !profile) {
            // Profile doesn't exist, create it
            console.warn('⚠️ [AuthStore] Profile not found during loadUser, creating...');
            const { data: newProfile, error: createError } = await db.userProfiles.create(
              session.session.user.id,
              {
                role: 'client',
                full_name: session.session.user.email?.split('@')[0] || '',
              }
            );
            
            if (createError) {
              console.error('❌ [AuthStore] Failed to create profile:', createError);
              set({ user: null, isAuthenticated: false, isLoading: false });
              return;
            }
            
            console.log('✅ [AuthStore] Profile created successfully');
            set({
              user: {
                id: session.session.user.id,
                email: session.session.user.email!,
                profile: newProfile as UserProfile,
              },
              isAuthenticated: true,
              isLoading: false,
            });
          } else {
            console.log('✅ [AuthStore] User loaded successfully');
            set({
              user: {
                id: session.session.user.id,
                email: session.session.user.email!,
                profile: profile as UserProfile,
              },
              isAuthenticated: true,
              isLoading: false,
            });
          }
        } catch (error) {
          console.error('❌ [AuthStore] Load user error:', error);
          set({ user: null, isAuthenticated: false, isLoading: false });
        } finally {
          isLoadingUser = false;
          loadUserPromise = null;
        }
        })();

        return loadUserPromise;
      },

      updateProfile: async (data: Partial<UserProfile>) => {
        try {
          const currentUser = get().user;
          if (!currentUser) throw new Error('No user logged in');

          const { data: updatedProfile, error } = await db.userProfiles.update(
            currentUser.id,
            data
          );

          if (error) throw error;

          set({
            user: {
              ...currentUser,
              profile: updatedProfile as UserProfile,
            },
          });
        } catch (error) {
          console.error('Update profile error:', error);
          throw error;
        }
      },
    }),
    {
      name: 'flowhub-auth',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // Mark as hydrated
        hasHydrated = true;
        
        // Called when state is restored from localStorage
        if (state) {
          console.log('🔄 [AuthStore] State rehydrated from localStorage', {
            hasUser: !!state.user,
            isAuthenticated: state.isAuthenticated,
          });
          // Don't set isLoading=false here — let loadUser validate the session first.
          // This prevents briefly showing authenticated UI with an expired session.
        } else {
          console.log('🔄 [AuthStore] State rehydrated - no persisted state');
        }
      },
    }
  )
);

// Initialize auth state on app load
auth.onAuthStateChange(async (event, session) => {
  // Não fazer auto-login durante processo de signup (evita redirect antes de validar perfil)
  if (isSigningUp) {
    console.log('Ignoring auth state change during signup process');
    return;
  }
  
  if (event === 'SIGNED_IN' && session) {
    // CRÍTICO: Verificar se MFA é necessário antes de carregar usuário
    const state = useAuthStore.getState();
    
    // Se já está aguardando MFA, não fazer nada
    if (state.mfaRequired) {
      console.log('MFA verification pending - skipping auto loadUser');
      return;
    }
    
    // Verificar se há fatores MFA verificados
    const { data: factors } = await auth.mfa.listFactors();
    const hasVerifiedFactors = factors?.totp?.some(f => f.status === 'verified');
    
    // Se há MFA configurado e não foi verificado nesta sessão
    if (hasVerifiedFactors) {
      const { data: aal } = await auth.mfa.getAuthenticatorAssuranceLevel();
      
      if (aal?.currentLevel === 'aal1') {
        console.log('MFA required but not verified - waiting for user input');
        // Não fazer loadUser - aguardar verificação manual
        return;
      }
    }
    
    // Sem MFA ou já verificado - pode carregar usuário
    console.log('Loading user from auth state change');
    await useAuthStore.getState().loadUser();
  } else if (event === 'SIGNED_OUT') {
    useAuthStore.setState({ user: null, isAuthenticated: false, isLoading: false });
  }
});
